import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { Observable, Subscription, interval, map, startWith, take, firstValueFrom } from 'rxjs';
import { ScreenSaverService } from '../../../services/screen-saver.service';
import { Chore } from '../../../interfaces/chore.interface';
import { Child } from '../../../interfaces/child.interface';
import { animate, style, transition, trigger } from '@angular/animations';
import { StickerService } from '../../../services/sticker.service';
import { Sticker } from '../../../interfaces/sticker.interface';
import { ColoringBookService } from '../../../services/coloring-book.service';
import { ColoringPage } from '../../../interfaces/coloring-page.interface';

// Extended ColoringPage type with optional creatorName
type ColoringPageWithCreator = ColoringPage & { creatorName?: string };

@Component({
  selector: 'app-screen-saver',
  standalone: true,
  imports: [CommonModule, MatCardModule, DatePipe],
  template: `
    <div 
      class="screen-saver-overlay" 
      *ngIf="isActive$ | async"
      (click)="deactivateScreenSaver()"
      [@fadeInOut]="'in'">
      
      <div class="datetime-display" [@fadeInOut]="'in'">
        <div class="date">{{ currentDate | date:'EEEE, MMMM d, y' }}</div>
        <div class="time">{{ currentDate | date:'h:mm a' }}</div>
      </div>
      
      <div class="content-container">
        <!-- Coloring Page Display -->
        <div class="coloring-page-display" *ngIf="currentColoringPage$ | async as coloringPage">
          <div class="coloring-page-card" [@fadeInOut]="'in'">
            <div class="coloring-page-image">
              <img [src]="coloringPage.imageUrl" alt="Coloring page">
            </div>
            
            <div class="coloring-page-details">
              <p class="creator-name" *ngIf="coloringPage.creatorName">Created by {{ coloringPage.creatorName }}</p>
            </div>
          </div>
        </div>
        
        <!-- Chore Display -->
        <div class="chore-display" *ngIf="currentChoreWithChild$ | async as item">
          <div class="chore-card" [@fadeInOut]="'in'">
            <h2>{{ item.chore.title }}</h2>
            
            <div class="chore-image" *ngIf="item.chore.photo">
              <img [src]="item.chore.photo" alt="Chore image">
            </div>
            
            <div class="chore-details">
              <p class="chore-description" *ngIf="item.chore.description">{{ item.chore.description }}</p>
              <p class="child-name">{{ item.child.displayName }}</p>
              <p class="chore-points">Points: {{ item.chore.straws }}</p>
            </div>
          </div>
        </div>
        
        <!-- Sticker Display -->
        <div class="sticker-display" *ngIf="currentSticker$ | async as sticker">
          <div class="sticker-card" [@fadeInOut]="'in'">
            <div class="sticker-image">
              <img [src]="sticker.imageUrl" alt="Sticker image">
            </div>
            <p class="sticker-creator" *ngIf="sticker.creatorName">
              Created by {{ sticker.creatorName }}
            </p>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .screen-saver-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.95);
      z-index: 9999;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
    
    .datetime-display {
      position: absolute;
      top: 3rem;
      right: 3rem;
      color: white;
      text-align: right;
      font-family: 'Arial', sans-serif;
      padding: 1rem;
    }
    
    .date {
      font-size: 2rem;
      margin-bottom: 1.5rem;
    }
    
    .time {
      font-size: 3.5rem;
      font-weight: bold;
    }
    
    .content-container {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      gap: 2rem;
    }
    
    .coloring-page-display, .sticker-display {
      width: 20%;
      max-width: 300px;
      margin: 1rem;
    }
    
    .chore-display {
      width: 60%;
      max-width: 800px;
    }
    
    .coloring-page-card, .sticker-card {
      background-color: rgba(255, 255, 255, 0.1);
      color: white;
      padding: 2rem;
      border-radius: 8px;
      text-align: center;
    }
    
    .chore-card {
      background-color: rgba(255, 255, 255, 0.1);
      color: white;
      padding: 2rem;
      border-radius: 8px;
      text-align: center;
      border: 2px solid rgba(204, 204, 204, 0.5); /* Light gray border */
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    }
    
    .coloring-page-image, .chore-image, .sticker-image {
      margin: 1rem 0;
      display: flex;
      justify-content: center;
    }
    
    .coloring-page-image img, .chore-image img, .sticker-image img {
      max-width: 100%;
      max-height: 300px;
      border-radius: 4px;
    }
    
    .chore-details {
      margin-top: 1rem;
    }
    
    .chore-description {
      font-style: italic;
      margin-bottom: 1rem;
    }
    
    .child-name {
      font-size: 2.5rem;
      font-weight: bold;
      margin: 1rem 0;
      color: #ffeb3b; /* Yellow highlight color */
    }
    
    .creator-name {
      font-size: 1rem;
      margin: 0.5rem 0;
      color: #cccccc; /* Light gray color */
    }
    
    .chore-points {
      font-weight: bold;
      font-size: 1.1rem;
    }
    
    .coloring-page-prompt {
      font-style: italic;
      margin-bottom: 1rem;
      font-size: 1.1rem;
    }
    
    .sticker-creator {
      font-size: 1rem;
      margin-top: 0.5rem;
      color: #cccccc; /* Light gray color */
    }
    
    h2 {
      font-size: 2rem;
      margin-bottom: 1rem;
    }
  `],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('500ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('500ms ease-out', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class ScreenSaverComponent implements OnInit, OnDestroy {
  isActive$: Observable<boolean>;
  choresWithChildren$: Observable<Array<{chore: Chore, child: Child}>>;  
  currentDate: Date = new Date();
  private clockSubscription: Subscription | null = null;
  currentChoreWithChild$: Observable<{chore: Chore, child: Child} | null>;
  currentSticker$: Observable<Sticker | null>;
  currentColoringPage$: Observable<ColoringPageWithCreator | null>;
  
  private rotationSubscription: Subscription | null = null;
  private stickerRotationSubscription: Subscription | null = null;
  private coloringPageRotationSubscription: Subscription | null = null;
  private currentIndex = 0;

  constructor(
    private screenSaverService: ScreenSaverService,
    private stickerService: StickerService,
    private coloringBookService: ColoringBookService
  ) {
    this.isActive$ = this.screenSaverService.isActive$;
    this.choresWithChildren$ = this.screenSaverService.getIncompleteChoresWithChildren();
    
    // Set up the current chore observable that will change every 10 seconds
    this.currentChoreWithChild$ = interval(10000).pipe(
      map(() => {
        return this.getRandomChoreWithChild();
      })
    );
    
    // Initialize with null values - will be populated in ngOnInit
    this.currentSticker$ = new Observable<Sticker | null>(observer => {
      observer.next(null);
    });
    
    this.currentColoringPage$ = new Observable<ColoringPageWithCreator | null>(observer => {
      observer.next(null);
    });
    
    // We'll set up the rotation in ngOnInit instead
  }

  ngOnInit(): void {
    // Initialize with a random chore when active
    this.currentChoreWithChild$ = this.choresWithChildren$.pipe(
      map(chores => {
        if (chores.length === 0) return null;
        return chores[Math.floor(Math.random() * chores.length)];
      })
    );
    
    // Subscribe to screen saver active state to control data loading
    this.isActive$.subscribe(isActive => {
      if (isActive) {
        // Screen saver is active - start all data loading and rotations
        
        // Set up rotation subscription - rotate chores every 10 seconds
        this.rotationSubscription = interval(10000).subscribe(() => {
          this.currentChoreWithChild$ = this.choresWithChildren$.pipe(
            map(chores => this.getRandomChoreWithChild())
          );
        });
        
        // Initial loads - only when screen saver becomes active
        this.loadRandomSticker();
        this.loadRandomColoringPage();
        
        // Set up sticker rotation - rotate stickers every 12 seconds
        this.stickerRotationSubscription = interval(12000).subscribe(() => {
          this.loadRandomSticker();
        });
        
        // Set up coloring page rotation - rotate coloring pages every 15 seconds
        this.coloringPageRotationSubscription = interval(15000).subscribe(() => {
          this.loadRandomColoringPage();
        });
      } else {
        // Screen saver is inactive - clean up subscriptions to stop data loading
        this.cleanupRotationSubscriptions();
      }
    });
    
    // Update the clock every second - needed whether active or not for display
    this.clockSubscription = interval(1000).pipe(
      startWith(0) // Emit immediately
    ).subscribe(() => {
      this.currentDate = new Date();
    });
  }

  ngOnDestroy(): void {
    // Clean up all subscriptions when component is destroyed
    this.cleanupRotationSubscriptions();
    
    if (this.clockSubscription) {
      this.clockSubscription.unsubscribe();
    }
  }
  
  /**
   * Cleans up all rotation-related subscriptions
   * Called when screen saver becomes inactive to prevent unnecessary data fetching
   */
  private cleanupRotationSubscriptions(): void {
    // Clean up chore rotation
    if (this.rotationSubscription) {
      this.rotationSubscription.unsubscribe();
      this.rotationSubscription = null;
    }
    
    // Clean up sticker rotation
    if (this.stickerRotationSubscription) {
      this.stickerRotationSubscription.unsubscribe();
      this.stickerRotationSubscription = null;
    }
    
    // Clean up coloring page rotation
    if (this.coloringPageRotationSubscription) {
      this.coloringPageRotationSubscription.unsubscribe();
      this.coloringPageRotationSubscription = null;
    }
  }

  deactivateScreenSaver(): void {
    this.screenSaverService.deactivateScreenSaver();
  }

  private getRandomChoreWithChild(): {chore: Chore, child: Child} | null {
    let chores: Array<{chore: Chore, child: Child}> = [];
    
    // Get the latest value from the observable
    const subscription = this.choresWithChildren$.subscribe(result => {
      chores = result;
    });
    subscription.unsubscribe();
    
    if (chores.length === 0) return null;
    
    // Get a random chore - truly random each time
    // This ensures we might show the same chore twice in a row sometimes,
    // but the overall selection is completely random
    return chores[Math.floor(Math.random() * chores.length)];
  }
  
  /**
   * Loads a random sticker and updates the currentSticker$ observable
   */
  private loadRandomSticker(): void {
    // Use firstValueFrom to get stickers and update the observable
    firstValueFrom(this.stickerService.getCommunityStickers().pipe(take(1)))
      .then(stickers => {
        if (stickers && stickers.length > 0) {
          const randomIndex = Math.floor(Math.random() * stickers.length);
          const randomSticker = stickers[randomIndex];
          
          // Update the observable with the new sticker
          this.currentSticker$ = new Observable<Sticker | null>(observer => {
            observer.next(randomSticker);
            observer.complete();
          });
        }
      })
      .catch(error => {
        console.error('Error loading stickers:', error);
      });
  }
  
  /**
   * Loads a random coloring page and updates the currentColoringPage$ observable
   */
  private loadRandomColoringPage(): void {
    // Use firstValueFrom to get coloring pages and update the observable
    firstValueFrom(this.coloringBookService.getCommunityColoringPages().pipe(take(1)))
      .then(coloringPages => {
        if (coloringPages && coloringPages.length > 0) {
          const randomIndex = Math.floor(Math.random() * coloringPages.length);
          const randomColoringPage = coloringPages[randomIndex] as ColoringPageWithCreator;
          
          // Update the observable with the new coloring page
          this.currentColoringPage$ = new Observable<ColoringPageWithCreator | null>(observer => {
            observer.next(randomColoringPage);
            observer.complete();
          });
        }
      })
      .catch(error => {
        console.error('Error loading coloring pages:', error);
      });
  }
}
